import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { binanceAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/fapi/v1/income',
    query: {
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // Sort by time in descending order
  const sortedItems = response.sort((a, b) => b.time - a.time)

  // Data processing
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedItems) {
    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? item.time : Math.min(fetchedFrom, item.time)
    fetchedTo =
      fetchedTo === undefined ? item.time : Math.max(fetchedTo, item.time)

    // Skip transfer types
    if (
      item.incomeType === 'TRANSFER' ||
      item.incomeType === 'CROSS_COLLATERAL_TRANSFER' ||
      item.incomeType === 'INTERNAL_TRANSFER' ||
      item.incomeType === 'STRATEGY_UMFUTURES_TRANSFER'
    ) {
      continue
    }

    // Handle REALIZED_PNL
    if (item.incomeType === 'REALIZED_PNL') {
      if (item.income.value !== 0n) {
        const asset = getAsset(item.asset)
        if (!asset) {
          throw new AssetNotFoundException(item.asset)
        }

        actions.push({
          comment: `Realized PL of ${item.symbol} (USD-M Futures)`,
          evidence: 'system-rule',
          order: 0,
          source: `${binanceAccountCategory}_${config.type}_${item.incomeType}_${item.tranId}`,
          type: item.income.value > 0n ? 'realize-profit' : 'realize-loss',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.income),
              direction: item.income.value > 0n ? 'in' : 'out',
            },
          ],
        })
      }
      continue
    }

    // Handle FUNDING_FEE
    if (item.incomeType === 'FUNDING_FEE') {
      if (item.income.value !== 0n) {
        const asset = getAsset(item.asset)
        if (!asset) {
          throw new AssetNotFoundException(item.asset)
        }

        actions.push({
          comment: `Funding of ${item.symbol} (USD-M Futures)`,
          evidence: 'system-rule',
          order: 0,
          source: `${binanceAccountCategory}_${config.type}_${item.incomeType}_${item.tranId}`,
          type: item.income.value > 0n ? 'derivative-profit' : 'derivative-fee',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.income),
              direction: item.income.value > 0n ? 'in' : 'out',
            },
          ],
        })
      }
      continue
    }

    // Handle COMMISSION
    if (item.incomeType === 'COMMISSION') {
      if (item.income.value < 0n) {
        const asset = getAsset(item.asset)
        if (!asset) {
          throw new AssetNotFoundException(item.asset)
        }

        actions.push({
          comment: `Trade Fee of ${item.symbol} (USD-M Futures)`,
          evidence: 'system-rule',
          order: 0,
          source: `${binanceAccountCategory}_${config.type}_${item.incomeType}_${item.tranId}`,
          type: 'fee',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.income),
              direction: 'out',
            },
          ],
        })
      }
      continue
    }

    // Handle INSURANCE_CLEAR
    if (item.incomeType === 'INSURANCE_CLEAR') {
      if (item.income.value !== 0n) {
        const asset = getAsset(item.asset)
        if (!asset) {
          throw new AssetNotFoundException(item.asset)
        }

        actions.push({
          comment: `Insurance Clear of ${item.symbol} (USD-M Futures)`,
          evidence: 'system-rule',
          order: 0,
          source: `${binanceAccountCategory}_${config.type}_${item.incomeType}_${item.tranId}`,
          type: item.income.value > 0n ? 'income' : 'fee',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.income),
              direction: item.income.value > 0n ? 'in' : 'out',
            },
          ],
        })
      }
      continue
    }

    // Handle unsupported income types
    throw new Error(`Unsupported incomeType: ${item.incomeType}`)
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
